/**
 * Resilient Fetch - API client with automatic retry and exponential backoff
 *
 * Features:
 * - Automatic retries for transient errors (429, 502, 503, 504)
 * - Exponential backoff with jitter
 * - Rate limit header extraction
 * - Integration with ApiHealthContext for status tracking
 */

import { RateLimitInfo, ServiceId } from '../types/api';

// Configuration
const RETRY_STATUS_CODES = new Set([429, 502, 503, 504]);
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
const JITTER_FACTOR = 0.2; // +/- 20% randomization

// Status codes that should NOT be retried (client errors)
const NO_RETRY_STATUS_CODES = new Set([400, 401, 403, 404, 422]);

/**
 * Calculate exponential backoff delay with jitter
 * @param attempt - Current retry attempt (0-based)
 * @param retryAfterHeader - Value of Retry-After header if present
 * @returns Delay in milliseconds
 */
export const calculateBackoff = (attempt: number, retryAfterHeader: string | null = null): number => {
  // If Retry-After header is present, use it
  if (retryAfterHeader) {
    const retryAfterSeconds = Number.parseInt(retryAfterHeader, 10);
    if (!Number.isNaN(retryAfterSeconds)) {
      return retryAfterSeconds * 1000;
    }
  }

  // Exponential backoff: 1s, 2s, 4s, 8s...
  const exponentialDelay = BASE_DELAY_MS * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, MAX_DELAY_MS);

  // Add jitter (+/- 20%)
  const jitter = cappedDelay * JITTER_FACTOR * (Math.random() * 2 - 1);
  return Math.round(cappedDelay + jitter);
};

/**
 * Extract rate limit information from response headers
 * @param headers - Response headers
 * @returns Rate limit info
 */
export const extractRateLimitHeaders = (headers: Headers): RateLimitInfo & { retryAfter: string | null } => {
  return {
    limitMinute: Number.parseInt(headers.get('X-RateLimit-Limit-Minute') ?? '', 10) || null,
    remainingMinute: Number.parseInt(headers.get('X-RateLimit-Remaining-Minute') ?? '', 10) || null,
    resetMinute: Number.parseInt(headers.get('X-RateLimit-Reset-Minute') ?? '', 10) || null,
    limitHour: Number.parseInt(headers.get('X-RateLimit-Limit-Hour') ?? '', 10) || null,
    remainingHour: Number.parseInt(headers.get('X-RateLimit-Remaining-Hour') ?? '', 10) || null,
    resetHour: Number.parseInt(headers.get('X-RateLimit-Reset-Hour') ?? '', 10) || null,
    retryAfter: headers.get('Retry-After') || null,
  };
};

/**
 * Determine if a request should be retried based on status code
 * @param status - HTTP status code
 * @param attempt - Current attempt number
 * @returns Whether to retry
 */
export const shouldRetry = (status: number, attempt: number): boolean => {
  if (attempt >= MAX_RETRIES) return false;
  if (NO_RETRY_STATUS_CODES.has(status)) return false;
  return RETRY_STATUS_CODES.has(status);
};

/**
 * Sleep for a specified duration
 * @param ms - Duration in milliseconds
 * @returns Resolves after the specified duration
 */
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Custom error class for API errors with additional context
 */
export class ApiError extends Error {
  status: number;
  rateLimitInfo: (RateLimitInfo & { retryAfter: string | null }) | null;
  isRetryable: boolean;

  constructor(
    status: number,
    message: string,
    rateLimitInfo: (RateLimitInfo & { retryAfter: string | null }) | null = null,
    isRetryable = false
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.rateLimitInfo = rateLimitInfo;
    this.isRetryable = isRetryable;
  }
}

export interface HealthCallbacks {
  onSuccess: (serviceId: ServiceId, rateLimitInfo: RateLimitInfo & { retryAfter: string | null }) => void;
  onFailure: (serviceId: ServiceId, status: number, message: string, isRetrying: boolean) => void;
  onRateLimitUpdate: (serviceId: ServiceId, rateLimitInfo: RateLimitInfo & { retryAfter: string | null }) => void;
}

// Callbacks for health tracking (set by ApiHealthContext)
let onRequestSuccess: HealthCallbacks['onSuccess'] | null = null;
let onRequestFailure: HealthCallbacks['onFailure'] | null = null;
let onRateLimitUpdate: HealthCallbacks['onRateLimitUpdate'] | null = null;

/**
 * Register callbacks for API health tracking
 * Called by ApiHealthContext on mount
 */
export const registerHealthCallbacks = (callbacks: HealthCallbacks): void => {
  onRequestSuccess = callbacks.onSuccess;
  onRequestFailure = callbacks.onFailure;
  onRateLimitUpdate = callbacks.onRateLimitUpdate;
};

/**
 * Unregister health callbacks
 * Called by ApiHealthContext on unmount
 */
export const unregisterHealthCallbacks = (): void => {
  onRequestSuccess = null;
  onRequestFailure = null;
  onRateLimitUpdate = null;
};

export interface ResilientFetchResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response data is untyped JSON; changing to unknown cascades through all consumers
  data: any;
  rateLimitInfo: RateLimitInfo & { retryAfter: string | null };
}

/**
 * Resilient fetch wrapper with automatic retry and backoff
 * @param url - Request URL
 * @param options - Fetch options
 * @param serviceId - Service identifier for health tracking ('genaiops' or 'core')
 * @returns Response data and rate limit info
 */
export const resilientFetch = async (
  url: string,
  options: RequestInit = {},
  serviceId: ServiceId = 'genaiops'
): Promise<ResilientFetchResult> => {
  let lastError: unknown = null;
  let attempt = 0;

  while (attempt <= MAX_RETRIES) {
    try {
      const response = await fetch(url, {
        ...options,
        signal: options.signal || AbortSignal.timeout(30000), // 30s default timeout
      });

      const rateLimitInfo = extractRateLimitHeaders(response.headers);

      // Update rate limit tracking
      if (onRateLimitUpdate && rateLimitInfo.remainingMinute !== null) {
        onRateLimitUpdate(serviceId, rateLimitInfo);
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        let errorData: { detail?: string; message?: string; error?: string };
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { detail: errorText };
        }

        const errorMessage = errorData.detail || errorData.message || errorData.error || `HTTP ${response.status}`;

        // Check if we should retry
        if (shouldRetry(response.status, attempt)) {
          const delay = calculateBackoff(attempt, rateLimitInfo.retryAfter);

          // Notify about retryable failure (but we're retrying)
          if (onRequestFailure) {
            onRequestFailure(serviceId, response.status, errorMessage, true);
          }

          await sleep(delay);
          attempt++;
          continue;
        }

        // Non-retryable error
        if (onRequestFailure) {
          onRequestFailure(serviceId, response.status, errorMessage, false);
        }

        throw new ApiError(response.status, errorMessage, rateLimitInfo, false);
      }

      // Success
      const data = await response.json().catch(() => ({}));

      if (onRequestSuccess) {
        onRequestSuccess(serviceId, rateLimitInfo);
      }

      return { data, rateLimitInfo };

    } catch (error) {
      // Handle network errors and timeouts
      if (error instanceof ApiError) {
        throw error;
      }

      const err = error instanceof Error ? error : new Error(String(error));

      const isTimeout = err.name === 'TimeoutError' || err.name === 'AbortError';
      const isNetworkError = err.name === 'TypeError' && err.message.includes('fetch');

      if ((isTimeout || isNetworkError) && attempt < MAX_RETRIES) {
        const delay = calculateBackoff(attempt);

        if (onRequestFailure) {
          onRequestFailure(serviceId, 0, err.message, true);
        }

        await sleep(delay);
        attempt++;
        lastError = error;
        continue;
      }

      // Final failure after all retries
      if (onRequestFailure) {
        onRequestFailure(serviceId, 0, err.message, false);
      }

      throw new ApiError(0, err.message || 'Network error', null, false);
    }
  }

  // Should not reach here, but just in case
  if (lastError instanceof ApiError) {
    throw lastError;
  }
  throw new ApiError(0, 'Max retries exceeded', null, false);
};

/**
 * Convenience wrapper that returns just the data (for backward compatibility)
 * @param url - Request URL
 * @param options - Fetch options
 * @param serviceId - Service identifier
 * @returns Response data
 */
export const resilientFetchData = async (
  url: string,
  options: RequestInit = {},
  serviceId: ServiceId = 'genaiops'
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- API response data is untyped JSON
): Promise<any> => {
  const { data } = await resilientFetch(url, options, serviceId);
  return data;
};

export default resilientFetch;
